import type { Category } from 'components/landing/MelodyCategory';
import ProductUtils from 'helpers/ProductUtils';
import type { ProductWithRelationsFromCalypso } from 'types/calypso';

export const MODULE_EVENT_NAME = 'module_view';

interface Brands {
  aboutText: string | null;
  brandPromo: string | null;
  brandUrl: string;
  cleanName: string;
  facetTree: string | null;
  headerImageUrl: string;
  id: string;
  imageUrl: string;
  name: string;
  realBrandId: string;
  recommendationSource: string;
}

interface ProductItem {
  sku: string;
  styleId: string;
  price: string;
  name: string;
  brand: string;
  category: string;
  subCategory: string;
  gender: string;
  subcategory2: string;
  list_price: number;
  on_sale: boolean;
}

interface Module {
  items: ProductItem[];
  name: string;
}

interface ModuleViewEvent {
  module: Module;
}

export const createModuleViewEvent = ({ moduleName, products }: { moduleName: string; products: ProductItem[] }): ModuleViewEvent => ({
  module: {
    items: products,
    name: moduleName
  }
});

interface NonProductModule {
  items: Pick<ProductItem, 'brand'>[] | Pick<ProductItem, 'category'>[];
  name: string;
  type: 'brand' | 'category';
}

interface NonProductModuleViewEvent {
  module: NonProductModule;
}

export const createNonProductModuleViewEvent = ({
  moduleName,
  items,
  type
}: {
  moduleName: string;
  items: Pick<ProductItem, 'brand'>[] | Pick<ProductItem, 'category'>[];
  type: 'brand' | 'category';
}): NonProductModuleViewEvent => ({
  module: {
    items,
    name: moduleName,
    type
  }
});

export const createProductItem = (
  params: ProductItem & {
    sku: string;
    styleId: string;
    price: string;
    name: string;
    brand: string;
    category: string;
    subCategory: string;
    gender: string;
    subcategory2: string;
    list_price: number;
    on_sale: boolean;
  }
): ProductItem => ({
  sku: params.sku,
  styleId: params.styleId,
  price: params.price,
  name: params.name,
  brand: params.brand,
  category: params.category,
  subCategory: params.subCategory,
  gender: params.gender,
  subcategory2: params.subcategory2,
  list_price: params.list_price,
  on_sale: params.on_sale
});

export const mapCategories = ({ categories }: { categories: Category[] }): Pick<ProductItem, 'category'>[] =>
  categories.map(category => ({
    category: category.name
  }));

export const mapBrands = ({ brands }: { brands: Brands[] }): Pick<ProductItem, 'brand'>[] =>
  brands.map(brand => ({
    brand: brand.cleanName
  }));

export const mapProductsWithRelationsFromCalypso = ({ products }: { products: ProductWithRelationsFromCalypso[] }): ProductItem[] =>
  products.map(product => ({
    sku: product.productId,
    styleId: product.styleId,
    price: product.price?.replace('$', ''),
    name: product.productName,
    brand: product.brandName,
    category: product.productType ?? '',
    subCategory: getSubCategory(product),
    gender: getGender(product) ?? '',
    subcategory2: getSubCategory(product),
    list_price: ProductUtils.priceToFloat(product.originalPrice) || 0,
    on_sale: isOnSale(product)
  }));

// Helper functions
const getSubCategory = (product: ProductWithRelationsFromCalypso): string => product.productType || '';

const getGender = (product: ProductWithRelationsFromCalypso): string | undefined => {
  if (product.txAttrFacet_Gender && product.txAttrFacet_Gender.length > 0) {
    const gender = product.txAttrFacet_Gender[0]?.toLowerCase();

    if (gender?.includes('women')) {
      return 'women';
    }
    if (gender?.includes('men')) {
      return 'men';
    }
    if (gender?.includes('boys') || gender?.includes('girls')) {
      return 'kids';
    }

    return gender;
  }
  return '';
};

const isOnSale = (product: ProductWithRelationsFromCalypso): boolean => parseFloat(product.percentOff) > 0;
